import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';

const SuperAdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    phone: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, propertiesRes, analyticsRes] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/properties'),
        api.get('/api/analytics/admin'),
      ]);
      setUsers(usersRes.data);
      setProperties(propertiesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/users', userForm);
      setShowUserForm(false);
      setUserForm({ name: '', email: '', password: '', role: 'admin', phone: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/api/users/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const handlePropertyStatus = async (id, availabilityStatus) => {
    try {
      await api.patch(`/api/properties/${id}/status`, { availabilityStatus });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update property');
    }
  };

  return (
    <DashboardLayout title="Super Admin Dashboard">
      {analytics && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Properties</h3>
            <div className="value">{analytics.totalProperties}</div>
          </div>
          <div className="stat-card">
            <h3>Total Users</h3>
            <div className="value">{analytics.users?.total || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Total Revenue</h3>
            <div className="value">${analytics.totalRevenue?.toLocaleString() || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Occupancy Rate</h3>
            <div className="value">{analytics.occupancyRate?.toFixed(1) || 0}%</div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Users Management</h2>
          <button onClick={() => setShowUserForm(!showUserForm)} className="btn btn-primary">
            {showUserForm ? 'Cancel' : 'Add User'}
          </button>
        </div>

        {showUserForm && (
          <form onSubmit={handleCreateUser} style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '4px' }}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="admin">Admin</option>
                <option value="operations">Operations</option>
                <option value="housekeeping">Housekeeping</option>
              </select>
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
              />
            </div>
            <button type="submit" className="btn btn-success">Create User</button>
          </form>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td><span className="badge badge-info">{user.role}</span></td>
                <td>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Properties Management</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Availability</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property) => (
              <tr key={property.id}>
                <td>{property.title}</td>
                <td>{property.ownerId}</td>
                <td>
                  <span className={`badge badge-${property.status === 'approved' ? 'success' : 'warning'}`}>
                    {property.status}
                  </span>
                </td>
                <td>
                  <span className={`badge badge-${property.availabilityStatus === 'available' ? 'success' : 'secondary'}`}>
                    {property.availabilityStatus}
                  </span>
                </td>
                <td>
                  <select
                    value={property.availabilityStatus}
                    onChange={(e) => handlePropertyStatus(property.id, e.target.value)}
                    className="btn btn-sm"
                  >
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="under_maintenance">Under Maintenance</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;

